import type { FC } from 'react';

import { toast } from 'react-toastify/unstyled';

import { GenericButton, GenericModal } from '@shared/components/ui/custom';
import { notify } from '@shared/utils';
import type { RTKQueryError } from '@shared/utils/api.types';

import { useDeleteApiKeyMutation } from '../../api/api-keys.api';

interface ConfirmDeleteApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  apiKeyId: number;
  apiKeyName: string;
}

export const ConfirmDeleteApiKeyModal: FC<ConfirmDeleteApiKeyModalProps> = ({
  open,
  onClose,
  apiKeyId,
  apiKeyName,
}) => {
  const [deleteApiKey, { isLoading, reset }] = useDeleteApiKeyMutation();

  const handleOnClose = () => {
    onClose();
    reset?.();
  };

  const handleDelete = async () => {
    try {
      await deleteApiKey(apiKeyId).unwrap();
      toast.success('API Key deleted successfully!');
      handleOnClose();
    } catch (err) {
      const error = err as RTKQueryError;
      notify({
        type: 'error',
        title: 'Failed to delete API Key',
        description: error?.data?.message ?? 'Unexpected error',
      });
    }
  };

  return (
    <GenericModal
      open={open}
      onOpenChange={handleOnClose}
      title={<span>Are you absolutely sure?</span>}
      description={
        <span>
          This action cannot be undone. This will permanently delete the API key{' '}
          <span className="font-semibold">{apiKeyName}</span>.
        </span>
      }
      contentProps={{ className: 'max-w-md' }}
    >
      <div className="flex justify-end gap-3 pt-4">
        <GenericButton variant="outline" onClick={handleOnClose} className="px-6" label="Cancel" disabled={isLoading} />
        <GenericButton
          variant="destructive"
          onClick={handleDelete}
          className="px-6"
          label="Delete API Key"
          isLoading={isLoading}
          disabled={isLoading}
        />
      </div>
    </GenericModal>
  );
};
